import { AdminShell } from '../components/AdminShell';
import { SectionMarker } from '@/components/brand/SectionMarker';
import { useAdminMetrics } from '../useAdminMetrics';
import { cn } from '@/lib/cn';

interface LaunchMetrics {
  generated_at: string;
  retention: Array<{ first_day: string; cohort_size: number; d1: number; d7: number; d30: number }> | null;
  message_starts_per_dau: Array<{ day: string; ratio: number }> | null;
  reply_rate_24h: Array<{ rate: number; conversations: number; replied: number }> | null;
  reports_per_1k_convs: Array<{ reports_per_1k: number; conversations_7d: number; reports_7d: number }> | null;
  density_3km_by_suburb: Array<{ suburb: string; live_profiles: number }> | null;
  premium_conv_by_venue_proximity: Array<{ venue_adjacent: number; paying_among_them: number; ratio: number }> | null;
}

// Audit's Sydney-launch gates (docs/deep-research-report.md table).
const GATES = {
  d30:           0.30,
  msgPerDau:     0.25,
  replyRate24h:  0.40,
  payingConv:    0.05,
};

const pct = (n: number | null | undefined): string =>
  typeof n === 'number' && Number.isFinite(n) ? `${Math.round(n * 100)}%` : '—';

const tone = (value: number | null | undefined, gate: number): 'green' | 'amber' | 'red' => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'red';
  if (value >= gate) return 'green';
  if (value >= gate * 0.7) return 'amber';
  return 'red';
};

const TONE_CLASS: Record<'green' | 'amber' | 'red', string> = {
  green: 'border-pink/40 bg-pink/[0.05] text-pink',
  amber: 'border-white/[0.30] bg-charcoal/60 text-text-primary',
  red:   'border-white/[0.10] bg-charcoal/40 text-text-muted',
};

const GateCard = ({
  label,
  value,
  threshold,
  meets,
}: {
  label: string;
  value: string;
  threshold: string;
  meets: 'green' | 'amber' | 'red';
}) => (
  <div className={cn('border p-5', TONE_CLASS[meets])}>
    <p className="font-display text-[10px] font-bold uppercase tracking-widest opacity-80">{label}</p>
    <p className="mt-2 font-display text-3xl font-black uppercase tracking-display">{value}</p>
    <p className="mt-2 text-[11px] opacity-80">Gate: {threshold}</p>
  </div>
);

export const AdminLaunchGatesPage = () => {
  const { data, loading, error } = useAdminMetrics<LaunchMetrics>('launch_gates');
  const m = data;

  const latestCohort = m?.retention?.[0];
  const latestMsg = m?.message_starts_per_dau?.[0];
  const reply = m?.reply_rate_24h?.[0];
  const reports = m?.reports_per_1k_convs?.[0];
  const premiumConv = m?.premium_conv_by_venue_proximity?.[0];

  return (
    <AdminShell eyebrow="Moderation · launch gates">
      <div className="px-5 py-7 md:px-12 md:py-10">
        <SectionMarker className="mb-3">Sydney readiness</SectionMarker>
        <h1 className="font-display text-3xl font-black uppercase tracking-display text-text-primary md:text-4xl">
          The gates<span className="text-pink">.</span>
        </h1>
        {m?.generated_at && (
          <p className="mt-2 font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            Updated {new Date(m.generated_at).toLocaleString()}
          </p>
        )}
        {error && (
          <p className="mt-4 font-display text-[10px] font-bold uppercase tracking-widest text-pink">— {error}</p>
        )}

        {loading && !m ? (
          <p className="mt-6 font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">Loading<span className="text-pink">.</span></p>
        ) : (
          <>
            <div className="mt-8 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <GateCard
                label="D30 retention"
                value={pct(latestCohort?.d30)}
                threshold="≥ 30%"
                meets={tone(latestCohort?.d30, GATES.d30)}
              />
              <GateCard
                label="Message starts / DAU"
                value={pct(latestMsg?.ratio)}
                threshold="≥ 25%"
                meets={tone(latestMsg?.ratio, GATES.msgPerDau)}
              />
              <GateCard
                label="Reply rate · 24h"
                value={pct(reply?.rate)}
                threshold="≥ 40%"
                meets={tone(reply?.rate, GATES.replyRate24h)}
              />
              <GateCard
                label="Premium conv · venue-adjacent"
                value={pct(premiumConv?.ratio)}
                threshold="≥ 5%"
                meets={tone(premiumConv?.ratio, GATES.payingConv)}
              />
            </div>

            <SectionMarker className="mb-3 mt-10">Trust cost of growth</SectionMarker>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className={cn('border p-5', 'border-white/[0.08] bg-charcoal/40')}>
                <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
                  Reports per 1k convs · 7d
                </p>
                <p className="mt-2 font-display text-3xl font-black uppercase tracking-display text-text-primary">
                  {reports?.reports_per_1k != null ? reports.reports_per_1k.toFixed(1) : '—'}
                </p>
                <p className="mt-2 text-[11px] text-text-secondary">
                  {reports?.reports_7d ?? 0} reports · {reports?.conversations_7d ?? 0} convs
                </p>
              </div>
            </div>

            <SectionMarker className="mb-3 mt-10">Density · top suburbs (live · 24h)</SectionMarker>
            <ul className="divide-y divide-white/[0.06] border border-white/[0.06]">
              {(m?.density_3km_by_suburb ?? []).slice(0, 10).map((row) => (
                <li key={row.suburb} className="flex items-center justify-between px-4 py-3">
                  <span className="font-display text-sm font-bold uppercase tracking-wide text-text-primary">
                    {row.suburb}
                  </span>
                  <span className="font-display text-[11px] font-bold uppercase tracking-widest text-pink">
                    {row.live_profiles}
                  </span>
                </li>
              ))}
              {(m?.density_3km_by_suburb ?? []).length === 0 && (
                <li className="px-4 py-3 text-xs text-text-muted">No live profiles in the last 24h.</li>
              )}
            </ul>
          </>
        )}
      </div>
    </AdminShell>
  );
};
